// ----- Lib -----
import * as THREE from 'three/webgpu';
import { NodeMaterial } from 'three/webgpu';
import { Raycaster, Vector2, Vector3 } from 'three';
import {
  // ----- Types -----
  float,
  vec2,
  vec3,
  vec4,
  // ----- Built-in Variables - GPUパイプラインが自動的に供給するデータ -----
  instanceIndex, // インスタンシング時、何番目のインスタンスかを返す(index)。粒子系で使う
  uv,
  positionLocal, // ローカル空間での頂点座標
  // ----- GPU Memory Management -----
  storage, // GPU側で読み書き可能なバッファ(
  uniform, // CPU→GPUで共有される定数
  // ----- Math & Interpolation Nodes -----
  length,
  mix,
  smoothstep,
  // ----- Control Flow Nodes -----
  Fn,
  If,
} from 'three/tsl';

// ----- Helper -----
import { computeCurl3D, computeMouseForce3D } from './helper/ParticleHelpers';

// ----- Parameters -----
const IS_MOBILE = window.matchMedia('(max-width: 767px)').matches;
// Particle
// パフォーマンス改善: パーティクル数を削減
const NUM_PARTICLES = IS_MOBILE ? 300000 : 500000;
const PARTICLE_SIZE = 2; // パーティクルサイズを大きくして視認性を向上
const PARTICLE_COLOR_BLUE = vec3(0.3, 0.6, 0.9);
const PARTICLE_COLOR_RED = vec3(0.6, 0.2, 0.2);
const PARTICLE_COLOR_WHITE = vec3(0.8, 0.8, 0.8);
// Canvas
const CANVAS_COLOR = 0x111111;
// Speed Parameters
const PARTICLE_SPEED = 0.6; // curl noiseベースの動きの速度（大きいほど速い）
const TIME_INCREMENT = 0.05; // 時間の進み方（大きいほど時間が早く進む）

const SPACE_BASE_SIZE = 500.0; // 3D空間の基準サイズ（高さ）


export class App {
  // ----- Elements -----
  private canvas: HTMLCanvasElement;
  // ----- Parameters -----
  private width: number;
  private height: number;
  private pixelRatio: number = 1;
  // ----- Setup ThreeJS -----
  private renderer: THREE.WebGPURenderer;
  private computeNode?: THREE.ComputeNode; // WebGPU Renderer で GPU Compute Shader を実行するためのノード
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  // ----- ThreeJS Uniforms -----
  private timeUniform = uniform(0, 'float'); // 時間
  private speedUniform = uniform(PARTICLE_SPEED, 'float'); // パーティクルの速度
  private mousePositionUniform = uniform(new Vector3(0, 0, 0), 'vec3'); // マウスの3D位置
  private mouseActiveUniform = uniform(0, 'float'); // マウスがアクティブかどうか（0: 非アクティブ, 1: アクティブ）
  private spaceWidthUniform = uniform(500.0, 'float'); // 画面比率に基づいた3D空間の幅
  private spaceHeightUniform = uniform(500.0, 'float'); // 画面比率に基づいた3D空間の高さ
  private spaceDepthUniform = uniform(500.0, 'float'); // 画面比率に基づいた3D空間の奥行き
  // ----- Space Dimensions -----
  private spaceWidth: number; // 画面比率に基づいた3D空間の幅
  private spaceHeight: number; // 画面比率に基づいた3D空間の高さ
  private spaceDepth: number; // 画面比率に基づいた3D空間の奥行き
  // ----- Mouse Interaction -----
  private mouse2D = new Vector2(); // マウスの2D位置（NDC座標）
  private raycaster = new Raycaster(); // レイキャスター

  constructor(canvas: HTMLCanvasElement) {
    // ----- Setup Canvas -----
    this.canvas = canvas;
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    // ----- Calculate Space Dimensions based on Aspect Ratio -----
    const aspectRatio = this.width / this.height;
    this.spaceHeight = SPACE_BASE_SIZE;
    this.spaceWidth = SPACE_BASE_SIZE * aspectRatio;
    this.spaceDepth = SPACE_BASE_SIZE;

    // ----- Update Uniforms -----
    this.spaceWidthUniform.value = this.spaceWidth;
    this.spaceHeightUniform.value = this.spaceHeight;
    this.spaceDepthUniform.value = this.spaceDepth;

    // ----- Setup ThreeJS -----
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, this.width / this.height, 0.1, 50000);
    // 3D空間の中央を見るようにカメラを配置
    const cameraDistance = IS_MOBILE ? 550 : 700;
    this.camera.position.set(0, 0, cameraDistance);
    this.camera.lookAt(0, 0, 0);
    this.camera.fov = 75;
    this.renderer = new THREE.WebGPURenderer({
      antialias: true,
      canvas: this.canvas,
    });

    // ----- Event Listeners -----
    window.addEventListener('resize', this.onResize);
    this.setupMouseInteraction();
  }

  /**
   * Appの初期化
   * @returns Promise<void>
   */
  public async initialize() {
    if (!this.renderer) return;

    // --------------------
    //  Setup
    // --------------------
    // ----- Setup Renderer -----
    await this.renderer.init();
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(this.width, this.height, false);
    this.renderer.setClearColor(CANVAS_COLOR, 1);

    // --------------------
    //  Data準備
    // --------------------
    // --- 位置と速度を格納するバッファ ---
    // パーティクルの初期位置を画面比率に基づいた3D空間に配置
    const positionData = new Float32Array(NUM_PARTICLES * 3);
    const velocityData = new Float32Array(NUM_PARTICLES * 3);
    for (let i = 0; i < NUM_PARTICLES; i++) {
      positionData[i * 3 + 0] = (Math.random() - 0.5) * this.spaceWidth; // pos x - 画面比率に基づいた幅
      positionData[i * 3 + 1] = (Math.random() - 0.5) * this.spaceHeight; // pos y - 画面比率に基づいた高さ
      positionData[i * 3 + 2] = (Math.random() - 0.5) * this.spaceDepth; // pos z - 画面比率に基づいた奥行き
      velocityData[i * 3 + 0] = 0; // vel x
      velocityData[i * 3 + 1] = 0; // vel y
      velocityData[i * 3 + 2] = 0; // vel z
    }

    // ----- Storage Buffer -----
    const positionBuffer = new THREE.StorageInstancedBufferAttribute(positionData, 3);
    const velocityBuffer = new THREE.StorageInstancedBufferAttribute(velocityData, 3);
    const positions = storage(positionBuffer, 'vec3', NUM_PARTICLES);
    const velocities = storage(velocityBuffer, 'vec3', NUM_PARTICLES);

    // ----- Compute Shader - TSL -----
    // TSLのFn()はクロージャで変数をキャプチャする形式
    const computeUpdate = Fn(() => {
      // ----- Dataの呼び出し -----
      const position = positions.element(instanceIndex).toVar(); // 中心位置を格納しているバッファ
      const velocity = velocities.element(instanceIndex).toVar(); // 速度を格納しているバッファ
      const pos = position.xyz.toVar();
      const vel = velocity.xyz.toVar(); // vec3なので.xyzを使用

      // ----- 位置の更新 -----
      // 3D curl noiseを使用
      const curlForce = computeCurl3D(pos, this.timeUniform);
      // 目標速度（curl force）
      const targetVel = curlForce.mul(this.speedUniform);

      // マウスフォースを計算（マウスがアクティブな場合のみ）
      const mouseForce = computeMouseForce3D(pos, this.mousePositionUniform);
      // マウスフォースを速度に加算
      const velWithMouseForce = targetVel.add(mouseForce.mul(this.mouseActiveUniform));

      // 現在の速度を少しずつ目標速度に近づける
      vel.assign(mix(vel, velWithMouseForce, 0.05));
      // 位置更新
      pos.addAssign(vel);

      // ----- 3D空間の境界処理（画面比率に基づいた直方体） -----
      // 画面比率に基づいた3D空間の範囲を超えた場合、反対側に移動させる
      const halfWidth = this.spaceWidthUniform.mul(0.5);
      const halfHeight = this.spaceHeightUniform.mul(0.5);
      const halfDepth = this.spaceDepthUniform.mul(0.5);

      // X方向の境界処理（画面比率に基づいた幅）
      If(pos.x.lessThan(halfWidth.negate()), () => {
        pos.x.addAssign(this.spaceWidthUniform);
      });
      If(pos.x.greaterThan(halfWidth), () => {
        pos.x.subAssign(this.spaceWidthUniform);
      });

      // Y方向の境界処理（高さ）
      If(pos.y.lessThan(halfHeight.negate()), () => {
        pos.y.addAssign(this.spaceHeightUniform);
      });
      If(pos.y.greaterThan(halfHeight), () => {
        pos.y.subAssign(this.spaceHeightUniform);
      });

      // Z方向の境界処理（奥行き）
      If(pos.z.lessThan(halfDepth.negate()), () => {
        pos.z.addAssign(this.spaceDepthUniform);
      });
      If(pos.z.greaterThan(halfDepth), () => {
        pos.z.subAssign(this.spaceDepthUniform);
      });

      // ----- バッファへの書き戻し -----
      positions.element(instanceIndex).assign(pos);
      velocities.element(instanceIndex).assign(vel);
    });

    // ----- Compute Node -----
    this.computeNode = computeUpdate().compute(NUM_PARTICLES);

    // --------------------
    //  Render
    // --------------------
    // ----- Geometry -----
    // パーティクルを正方形のビルボードとして描画
    const geometry = new THREE.PlaneGeometry(1, 1);
    geometry.setAttribute('positionData', positionBuffer);
    geometry.setAttribute('velocityData', velocityBuffer);

    // ----- Material -----
    const material = new NodeMaterial();
    material.transparent = true; // 透明度を有効化
    material.depthWrite = false; // 深度バッファへの書き込みを無効化

    // --------------------
    //  Vertex Shader
    // --------------------
    // positionDataとvelocityDataはgeometry.setAttributeで設定した属性名
    const particlePos = positions.element(instanceIndex);
    const particleVel = velocities.element(instanceIndex);
    // 3D位置にパーティクルサイズを加算（ビルボードとして表示）
    material.positionNode = particlePos.add(vec3(positionLocal.x.mul(PARTICLE_SIZE), positionLocal.y.mul(PARTICLE_SIZE), float(0.0)));

    // --------------------
    //  Fragment Shader
    // --------------------
    const speed = length(particleVel); // 速さの大きさ（3D）

    // 閾値の設定
    const midSpeed = float(3.0); // 中速
    const maxSpeed = float(5.0); // 高速

    // ブレンド率の計算
    const mixLowToMid = smoothstep(0.0, midSpeed, speed); // 第1段階
    const mixMidToHigh = smoothstep(midSpeed, maxSpeed, speed); // 第2段階

    // カラーミキシング
    let finalColor = mix(PARTICLE_COLOR_BLUE, PARTICLE_COLOR_WHITE, mixLowToMid);
    finalColor = mix(finalColor, PARTICLE_COLOR_RED, mixMidToHigh);

    // 円形マスク
    material.fragmentNode = Fn(() => {
      const localPos = uv().sub(vec2(0.5, 0.5)); // uv()は0から1の範囲なので、-0.5から0.5の範囲に変換 - sub()は引き算
      const dist = length(localPos);
      /**
       * mix()で条件分岐 - 外側を透過
       * -- 第1引数: 条件が成り立たない場合の値
       * -- 第2引数: 条件が成り立つ場合の値
       * -- 第3引数: 条件式
       */
      const alpha = smoothstep(float(0.3), float(0.5), dist);
      return vec4(finalColor, alpha.oneMinus()); // oneMinus()は1から引き算
    })();

    // ----- Mesh -----
    const mesh = new THREE.InstancedMesh(geometry, material, NUM_PARTICLES);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    this.renderer.setAnimationLoop(() => {
      this.animate();
    });
  }

  private animate = () => {
    this.timeUniform.value += TIME_INCREMENT;
    this.renderer.compute(this.computeNode as THREE.ComputeNode);
    this.renderer.render(this.scene, this.camera);
  }

  private onResize = () => {
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    const nextPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    if (nextPixelRatio !== this.pixelRatio) {
      this.pixelRatio = nextPixelRatio;
      this.renderer.setPixelRatio(this.pixelRatio);
    }

    // ----- Update Space Dimensions based on new Aspect Ratio -----
    const aspectRatio = this.width / this.height;
    this.spaceHeight = SPACE_BASE_SIZE;
    this.spaceWidth = SPACE_BASE_SIZE * aspectRatio;
    this.spaceDepth = SPACE_BASE_SIZE;

    // ----- Update Uniforms -----
    this.spaceWidthUniform.value = this.spaceWidth;
    this.spaceHeightUniform.value = this.spaceHeight;
    this.spaceDepthUniform.value = this.spaceDepth;

    this.renderer.setSize(this.width, this.height, false);
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * マウスインタラクションのセットアップ
   * マウス位置を3D空間に変換してuniformに設定
   */
  private setupMouseInteraction = () => {
    const handleMouseMove = (event: MouseEvent) => {
      const rect = this.canvas.getBoundingClientRect();
      // NDC座標系に変換（-1から1の範囲）
      this.mouse2D.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse2D.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // マウス位置を3D空間に変換
      this.updateMousePosition3D();
      this.mouseActiveUniform.value = 1.0;
    };

    const handleMouseLeave = () => {
      // マウスが離れたら非アクティブにする
      this.mouseActiveUniform.value = 0.0;
    };

    this.canvas.addEventListener('mousemove', handleMouseMove);
    this.canvas.addEventListener('mouseleave', handleMouseLeave);
  }

  /**
   * マウスの2D位置を3D空間に変換
   * レイキャスターでz=0の平面との交点を取得し、その位置を固定
   */
  private updateMousePosition3D = () => {
    // レイキャスターを使って3D空間の位置を取得
    this.raycaster.setFromCamera(this.mouse2D, this.camera);

    // レイとz=0の平面との交点を計算
    // レイの方程式: P = origin + t * direction
    // z = 0 の平面との交点を求める
    const ray = this.raycaster.ray;
    const origin = ray.origin;
    const direction = ray.direction;

    // z=0の平面との交点を計算
    // origin.z + t * direction.z = 0
    // t = -origin.z / direction.z
    if (Math.abs(direction.z) > 0.0001) {
      const t = -origin.z / direction.z;
      const worldPosition = origin.clone().add(direction.multiplyScalar(t));

      // uniformに設定（この位置を固定して反発力の中心とする）
      this.mousePositionUniform.value.copy(worldPosition);
    }
  }

}
